import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

interface RoomOption {
  id: string;
  name: string;
  room_type: string;
}

interface AddBookingModalProps {
  onClose: () => void;
  onSubmit: (data: {
    room_id: string;
    guest_display_name: string;
    arrival_date: string;
    departure_date: string;
    adults: number;
    booking_channel: string;
    children: number;
    infants: number;
    arrival_time: string;
    departure_time: string;
  }) => Promise<boolean>;
}

const MANUAL_CHANNELS = [
  { value: 'direct', label: '直接予約' },
  { value: 'airbnb', label: 'Airbnb' },
  { value: 'phone', label: '電話' },
  { value: 'walk_in', label: '飛び込み' },
  { value: 'other', label: 'その他' },
];

export default function AddBookingModal({ onClose, onSubmit }: AddBookingModalProps) {
  const [rooms, setRooms] = useState<RoomOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [roomId, setRoomId] = useState('');
  const [guestName, setGuestName] = useState('');
  const [arrivalDate, setArrivalDate] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [arrivalTime, setArrivalTime] = useState('15:00');
  const [departureTime, setDepartureTime] = useState('10:00');
  const [channel, setChannel] = useState('direct');

  useEffect(() => {
    supabase
      .from('rooms')
      .select('id, name, room_type')
      .eq('is_active', true)
      .order('name')
      .then(({ data }) => {
        if (data) setRooms(data as RoomOption[]);
      });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!roomId || !guestName.trim() || !arrivalDate || !departureDate || adults < 1) {
      setError('必須項目をすべて入力してください。');
      return;
    }

    if (departureDate <= arrivalDate) {
      setError('出発日は到着日より後にしてください。');
      return;
    }

    setLoading(true);
    const success = await onSubmit({
      room_id: roomId,
      guest_display_name: guestName.trim(),
      arrival_date: arrivalDate,
      departure_date: departureDate,
      adults,
      booking_channel: channel,
      children,
      infants,
      arrival_time: arrivalTime + ':00',
      departure_time: departureTime + ':00',
    });
    setLoading(false);

    if (success) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-background-50 rounded-xl shadow-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-200/70">
          <h3 className="text-sm font-semibold text-foreground-900">手動予約追加</h3>
          <button
            onClick={onClose}
            disabled={loading}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-400 transition-colors cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1.5">ゲスト名 <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              placeholder="例: 山田 太郎"
              className="w-full px-3 py-2 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1.5">部屋 <span className="text-red-500">*</span></label>
            <select
              value={roomId}
              onChange={(e) => setRoomId(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 cursor-pointer"
              required
            >
              <option value="">選択してください</option>
              {rooms.map((r) => (
                <option key={r.id} value={r.id}>{r.name} ({r.room_type})</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">到着日 <span className="text-red-500">*</span></label>
              <input
                type="date"
                value={arrivalDate}
                onChange={(e) => setArrivalDate(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">到着時刻</label>
              <input
                type="time"
                value={arrivalTime}
                onChange={(e) => setArrivalTime(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">出発日 <span className="text-red-500">*</span></label>
              <input
                type="date"
                value={departureDate}
                onChange={(e) => setDepartureDate(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">出発時刻</label>
              <input
                type="time"
                value={departureTime}
                onChange={(e) => setDepartureTime(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">大人 <span className="text-red-500">*</span></label>
              <input
                type="number"
                min={1}
                max={20}
                value={adults}
                onChange={(e) => setAdults(parseInt(e.target.value) || 1)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 text-center"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">子供</label>
              <input
                type="number"
                min={0}
                max={20}
                value={children}
                onChange={(e) => setChildren(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 text-center"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">幼児</label>
              <input
                type="number"
                min={0}
                max={10}
                value={infants}
                onChange={(e) => setInfants(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 text-center"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1.5">予約経路 <span className="text-red-500">*</span></label>
            <select
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 cursor-pointer"
              required
            >
              {MANUAL_CHANNELS.map((ch) => (
                <option key={ch.value} value={ch.value}>{ch.label}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 rounded-lg bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              キャンセル
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading && (
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-loader-4-line animate-spin"></i>
                </span>
              )}
              予約を追加
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}